import React, { useEffect, useState } from "react";
import {
  AutocompleteInput,
  BooleanInput,
  DateField,
  DateInput,
  Edit,
  NumberInput,
  SelectInput,
  SimpleForm,
  TextInput,
  useNotify,
  useRecordContext,
} from "react-admin";
import { useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FilledInput,
  IconButton,
  InputAdornment,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import GennevApiService from "../../services/gennev-api.service";
// RichTextInput removed - using TextInput instead
import { get } from "lodash";
import { UserRoleEdit } from "./userRoleEdit";

const UserTitle = () => {
  const record = useRecordContext();
  return (
    <span>
      User {record ? `"${record.last_name} ${record.first_name}"` : ""}
    </span>
  );
};

const ResetPassword = ({ record, setError, setSuccess }) => {
  const [disabled, setDisabled] = useState(() => false);
  const [confirmation, setConfirmation] = useState(() => false);

  const handleResetPassword = (email) => {
    setSuccess(() => "");
    setError(() => "");
    setDisabled(() => true);
    GennevApiService.resetPassword(email)
      .then((response) => {
        setDisabled(() => false);
        if (response.status === 200)
          setSuccess(() => "Reset password email sent");
      })
      .catch((error) => {
        setDisabled(() => false);
        setError(() =>
          get(
            error,
            "response.data.errors.message",
            "There was an error sending the reset email"
          )
        );
      });
  };

  return (
    <div>
      <Button
        onClick={() => setConfirmation(() => true)}
        disabled={disabled}
        variant="contained"
        color="primary"
        style={{ marginBottom: "1.5rem", width: "16rem" }}
      >
        <EmailIcon style={{ marginRight: ".5rem", fontSize: "medium" }} />
        Reset Password
      </Button>
      <Confirmation
        open={confirmation}
        setConfirmation={setConfirmation}
        handleConfirmation={() => handleResetPassword(record.email)}
        confirmationMessage={
          "This action will send an email to the user with a link to reset the password. Are you sure you want to continue?"
        }
      />
    </div>
  );
};
const SetPassword = ({ setError, setSuccess }) => {
  const [password, setPassword] = useState(() => "");
  const [showPassword, setShowPassword] = useState(() => false);
  const [disabled, setDisabled] = useState(() => false);
  const [confirmation, setConfirmation] = useState(() => false);
  const record = useRecordContext();

  const handleSetPassword = (userId, password) => {
    setSuccess(() => "");
    setError(() => "");
    setDisabled(() => true);
    GennevApiService.setPassword(userId, password)
      .then((response) => {
        setDisabled(() => false);
        setPassword(() => "");
        if (response.status === 200)
          setSuccess(() => "Password has been set successfully");
      })
      .catch((error) => {
        setDisabled(() => false);
        setError(() =>
          get(
            error,
            "response.data.errors.message",
            "There was an error setting the password"
          )
        );
      });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FilledInput
        style={{ marginTop: "1.5rem", width: "16rem" }}
        type={showPassword ? "text" : "password"}
        value={password}
        autoComplete="false"
        onChange={(e) => setPassword(e.target.value)}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={() =>
                setShowPassword((prevShowPassword) => !prevShowPassword)
              }
              onMouseDown={(e) => e.preventDefault()}
              edge="end"
              size="large"
            >
              {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </InputAdornment>
        }
      />

      <Button
        onClick={() => setConfirmation(() => true)}
        disabled={disabled}
        style={{ marginTop: ".5rem", width: "16rem" }}
        variant="contained"
        color="primary"
      >
        Set Password
      </Button>
      <Confirmation
        open={confirmation}
        setConfirmation={setConfirmation}
        handleConfirmation={() => handleSetPassword(record.id, password)}
        confirmationMessage={
          "This action will set a new password for the user. Are you sure you want to continue?"
        }
      />
    </div>
  );
};

const Aside = ({ setError, setSuccess }) => {
  const record = useRecordContext();
  return (
    <div style={{ width: 250, margin: "1em" }}>
      <h5>User details</h5>
      {record && (
        <div>
          <p>
            Created: <DateField source="created_at" record={record} showTime />
          </p>
          <p>
            Updated: <DateField source="updated_at" record={record} showTime />
          </p>
          <Divider />
          <SetPassword setError={setError} setSuccess={setSuccess} />
          <Divider sx={{ my: 2 }} />
          <ResetPassword
            record={record}
            setError={setError}
            setSuccess={setSuccess}
            style={{ marginTop: "30px" }}
          />
          <Divider sx={{ my: 2 }} />
          <UserRoleEdit />
        </div>
      )}
    </div>
  );
};

const Confirmation = (props) => {
  const { handleConfirmation, setConfirmation, open, confirmationMessage } =
    props;

  const handleConfirm = () => {
    handleConfirmation();
    setConfirmation(() => false);
  };

  const handleDecline = () => {
    setConfirmation(() => false);
  };

  return (
    <Dialog
      aria-labelledby="simple-dialog-title"
      open={open}
      onClose={handleDecline}
    >
      <DialogTitle id="alert-dialog-title">{"Confirmation"}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {confirmationMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleDecline()} color="primary" autoFocus>
          Decline
        </Button>
        <Button onClick={() => handleConfirm()} color="primary">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const UserEdit = () => {
  const [error, setError] = useState(() => "");
  const [success, setSuccess] = useState(() => "");
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState(null);

  const notify = useNotify();
  const userId = useParams().id;

  useEffect(() => {
    GennevApiService.getStates().then(({ data }) => setStates(data));
    GennevApiService.getUser(userId).then((userData) => {
      if (!userData.user.state_id) return;
      GennevApiService.getCities(userData.user.state_id).then(({ cities }) =>
        setCities(cities)
      );
    });
  }, [userId]);

  useEffect(() => {
    if (!selectedStateId) return; // set by default to null
    setCities([]);// to disable cites when state is changed until new cities are loaded
    GennevApiService.getCities(selectedStateId).then(({ cities }) =>
      setCities(cities)
    );
  }, [selectedStateId]);

  const onError = (error) => {
    // Show the server error message if it exists
    notify(get(error, "body.errors.message", "Error creating the user"), {
      type: "error",
    });
  };

  const transform = (data) => {
    const allowedFields = [
      "first_name",
      "last_name",
      "nickname",
      "about_me",
      "birthdate",
      "is_active",
      "city_id",
      "state_id",
      "country_id",
      "phone_number",
      "gender",
      "progyny_id",
    ];
    Object.keys(data).forEach(
      (field) => !allowedFields.includes(field) && delete data[field]
    );
    return {
      ...data,
      first_name: data["first_name"] || "",
      last_name: data["last_name"] || "",
      nickname: data["nickname"] || "",
      about_me: data["about_me"] || "",
      city_id: data["city_id"] || 0,
      state_id: data["state_id"] || 0,
      country_id: data["country_id"] || 0,
    };
  };

  useEffect(() => {
    error && notify(error, { type: "error" });
    success && notify(success);
  }, [error, success, notify]);

  return (
    <Edit
      title={<UserTitle />}
      aside={<Aside setError={setError} setSuccess={setSuccess} />}
      setSuccess={setSuccess}
      transform={transform}
      mutationOptions={{ onError }}
    >
      <SimpleForm>
        <TextInput disabled source="id" />
        <TextInput disabled source="email" />
        <TextInput source="first_name" />
        <TextInput source="last_name" />
        <BooleanInput source="is_active" />
        <TextInput source="nickname" />
        <TextInput source="about_me" multiline rows={4} />
        <DateInput source="birthdate" />

        <SelectInput
          source="state_id"
          // TODO Review the endpoint, it's not saving the state either using the state id nor the state name. However the endpoint only admits an string for the state or returns an error.
          choices={states.map(({ id, name }) => ({ id, name }))}
          onChange={(e) => setSelectedStateId(e.target.value)}
        />
        <AutocompleteInput
          source="city_id"
          choices={cities.map(({ id, name }) => ({ id, name }))}
        />
        <SelectInput
          source="gender"
          choices={[
            { id: "male", name: "Male" },
            { id: "female", name: "Female" },
          ]}
        />
        <TextInput source="phone_number" autocomoplete="off" />
        <NumberInput source="progyny_id" autocomoplete="off" />
      </SimpleForm>
    </Edit>
  );
};
